import React from 'react';
import { useCartContext } from 'context/CartContext/CartContext';
import { ReactComponent as PlusIcon } from 'assets/icons/svg/plus.svg';
import { ReactComponent as MenuIcon } from 'assets/icons/svg/menu.svg';
import { ReactComponent as CartIcon } from 'assets/icons/svg/cart.svg';
import { ReactComponent as MinusIcon } from 'assets/icons/svg/minus.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/svg/close.svg';
import { ReactComponent as BackIcon } from 'assets/icons/svg/chevron-left.svg';
import { ReactComponent as ClipboardIcon } from 'assets/icons/svg/clipboard.svg';

type IconButtonProps = {
  iconName: 'close' | 'menu' | 'cart' | 'back' | 'plus' | 'minus' | 'clipboard';
  borderStyle?: 'largeCorner' | 'smallCorner' | 'noCorner';
  variant: 'blank' | 'white' | 'themed' | 'outlined';
  onClick?: () => void;
  iconColor?: string;
  disabled?: boolean;
  styles?: string;
  size?: number;
};

const variants = {
  blank: 'bg-transparent',
  white: 'bg-white shadow-md',
  themed: 'bg-primary',
  outlined: 'bg-transparent border border-primary border-solid',
};

const borders = {
  largeCorner: 'rounded-full',
  smallCorner: 'rounded-[10px]',
  noCorner: 'rounded-none',
};

const ariaLabels = {
  close: 'Close',
  menu: 'Menu',
  cart: 'Cart',
  back: 'Back',
  plus: 'Plus',
  minus: 'Minus',
  clipboard: 'Copy to clipboard',
};

const IconButton: React.FC<IconButtonProps> = ({
  size,
  onClick,
  iconName,
  disabled,
  iconColor,
  styles = '',
  variant = 'themed',
  borderStyle = 'largeCorner',
}) => {
  const { cartProducts, cartQuantity } = useCartContext();
  let variantClasses: string = variants[variant];
  let borderClasses: string = borders[borderStyle];
  let ariaLabel: string = ariaLabels[iconName];

  const getIcon = (iconColor: string, size: number) => {
    switch (iconName) {
      case 'menu':
        return (
          <div>
            <MenuIcon aria-hidden width={size} height={size} fill={iconColor} />
            {cartProducts.length > 0 && (
              <div className='absolute -top-1 -right-1 w-[19px] h-[19px] flex items-center justify-center rounded-full bg-white'>
                <span className='font-semibold text-[9px] leading-[13px] text-black'>
                  {cartQuantity}
                </span>
              </div>
            )}
          </div>
        );
      case 'cart':
        return (
          <div>
            <CartIcon aria-hidden width={size} height={size} fill={iconColor} />
            {cartProducts.length > 0 && (
              <div className='absolute -top-1 -right-1 w-[19px] h-[19px] flex items-center justify-center rounded-full bg-white'>
                <span className='font-semibold text-[9px] leading-[13px] text-black'>
                  {cartQuantity}
                </span>
              </div>
            )}
          </div>
        );
      case 'back':
        return (
          <BackIcon aria-hidden width={size} height={size} fill={iconColor} />
        );
      case 'plus':
        return (
          <PlusIcon aria-hidden width={size} height={size} fill={iconColor} />
        );
      case 'minus':
        return (
          <MinusIcon aria-hidden width={size} height={size} fill={iconColor} />
        );
      case 'clipboard':
        return (
          <ClipboardIcon
            aria-hidden
            width={size}
            height={size}
            fill={iconColor}
          />
        );
      default:
        return (
          <CloseIcon aria-hidden width={size} height={size} fill={iconColor} />
        );
    }
  };

  return (
    <button
      className={`
        w-max h-max p-3 flex items-center justify-center cursor-pointer disabled:bg-disabledButton duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-black/60 
        ${variantClasses} 
        ${borderClasses} 
        ${styles}
      `}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      type='button'
    >
      {getIcon(iconColor || '', size || 28)}
    </button>
  );
};

export default IconButton;
